import { safeSanitize } from '../../utils/safe_sanitize';

angular.module("LeasePilot").directive("assignBuildingsModal", [
  "$http",
  "$mdDialog",
  "PartnersService",
  function($http, $mdDialog, PartnersService) {
    return {
      restrict: "E",
      scope: {
        partnerId: "@",
        partnerName: "@",
        buildings: "@"
      },
      templateUrl: 'assign_buildings_modal.html',
      controller: [
        "$scope",
        function($scope) {
          // Helper function to sanitize text input to prevent XSS and malicious code
          function sanitizeInput(text) {
            if (!text || typeof text !== 'string') return text || '';
            // Remove Angular interpolation markers first to prevent expression evaluation
            let cleanText = text.replace(/{{.*?}}/g, '');
            // Strip all HTML tags and attributes to prevent XSS
            cleanText = safeSanitize(cleanText, {
              allowedTags: [],
              allowedAttributes: [],
            });
            return cleanText.trim();
          }

          // Sanitize partnerName when it comes from attributes
          const sanitizedPartnerName = sanitizeInput($scope.partnerName || '');

          $scope.isProcessing = false;
          $scope.error = null;
          $scope.showOnlyAssigned = false;
          $scope.searchQuery = '';
          $scope.selectAll = false;
          
          // Get all buildings from the window object
          const allBuildings = window.buildings || [];
          const initialBuildingIds = new Set($scope.buildings.split(",").map(id => parseInt(id)));
          
          // Create the full list of buildings with selection state
          // Sanitize building names from the database
          $scope.availableBuildings = allBuildings.map(building => ({
            id: building.id,
            name: sanitizeInput(building.name || ''),
            selected: initialBuildingIds.has(building.id),
            initiallySelected: initialBuildingIds.has(building.id) // Keep track of initial state
          }));

          // Filter function for buildings
          $scope.buildingFilter = function(building) {
            if (!$scope.showOnlyAssigned) return true;
            return building.selected;
          };

          // Toggle select all functionality
          $scope.toggleSelectAll = function() {
            const sanitizedQuery = sanitizeInput($scope.searchQuery || '').toLowerCase();
            const filteredBuildings = $scope.availableBuildings.filter(building => 
              $scope.buildingFilter(building) && 
              building.name.toLowerCase().includes(sanitizedQuery)
            );
            
            filteredBuildings.forEach(building => {
              building.selected = $scope.selectAll;
            });
          };

          // Watch for changes in search or filter to update select all state
          $scope.$watchGroup(['searchQuery', 'showOnlyAssigned'], function() {
            const sanitizedQuery = sanitizeInput($scope.searchQuery || '').toLowerCase();
            const filteredBuildings = $scope.availableBuildings.filter(building => 
              $scope.buildingFilter(building) && 
              building.name.toLowerCase().includes(sanitizedQuery)
            );
            
            $scope.selectAll = filteredBuildings.length > 0 && 
                              filteredBuildings.every(building => building.selected);
          });

          $scope.cancel = function() {
            $mdDialog.cancel();
          };

          $scope.save = async function() {
            $scope.isProcessing = true;
            $scope.error = null;

            try {
              // Determine which buildings were added and removed
              const newlyAssignedBuildings = $scope.availableBuildings
                .filter(building => building.selected && !building.initiallySelected)
                .map(building => ({
                  id: building.id,
                  name: building.name
                }));

              const newlyUnassignedBuildings = $scope.availableBuildings
                .filter(building => !building.selected && building.initiallySelected)
                .map(building => ({
                  id: building.id,
                  name: building.name
                }));

              // Get the final list of selected buildings
              const selectedBuildingIds = $scope.availableBuildings
                .filter(building => building.selected)
                .map(building => building.id);

              await PartnersService.assignBuildings($scope.partnerId, selectedBuildingIds);

              // Track assignment changes
              if (newlyAssignedBuildings.length > 0) {
                // Sanitize building names before tracking
                const sanitizedAssignedBuildings = newlyAssignedBuildings.map(building => ({
                  id: building.id,
                  name: sanitizeInput(building.name || '')
                }));
                window.track.event(new AssignBuildingsEvent({
                  partner: {
                    id: $scope.partnerId,
                    name: sanitizedPartnerName
                  },
                  buildings: sanitizedAssignedBuildings
                }));
              }

              // Track unassignment changes
              if (newlyUnassignedBuildings.length > 0) {
                // Sanitize building names before tracking
                const sanitizedUnassignedBuildings = newlyUnassignedBuildings.map(building => ({
                  id: building.id,
                  name: sanitizeInput(building.name || '')
                }));
                window.track.event(new UnassignBuildingsEvent({
                  partner: {
                    id: $scope.partnerId,
                    name: sanitizedPartnerName
                  },
                  buildings: sanitizedUnassignedBuildings
                }));
              }
              
              // Close the dialog before reloading to prevent visual glitches
              $mdDialog.hide();
              window.location.reload();
            } catch (error) {
              console.error("Error assigning buildings:", error);
              $scope.error = "Failed to assign buildings. Please try again.";
              $scope.isProcessing = false;
            }
          };
        }
      ]
    };
  }
]); 